/**
 * Application configuration settings
 *
 * This file centralizes configuration values that may differ between environments
 * or that may need to be adjusted in the future.
 */

/**
 * Safely access environment variables in both browser and Node environments
 */
export const getEnvVar = (name: string, defaultValue: string = ''): string => {
  // For Vite, environment variables are prefixed with VITE_
  // and accessed through import.meta.env instead of process.env
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    // Check both with and without VITE_ prefix to support different deployment environments
    return (
      import.meta.env[`VITE_${name}`] as string ||
      import.meta.env[name] as string ||
      defaultValue
    );
  }

  // Fallback for other environments
  return defaultValue;
};

// Determine if we're in a production environment
const isProd = (): boolean => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env.PROD === true;
  }
  return false;
};

/**
 * Configuration values for the application
 */
export const config = {
  /**
   * Base URL for magic links, fallback to current origin if not specified
   * This should be set to the public-facing URL of the application
   */
  magicLinkBaseUrl: getEnvVar('MAGIC_LINK_BASE_URL', window.location.origin),

  /**
   * Email for testing, use this for all notifications in the MVP
   */
  testEmail: getEnvVar('TEST_EMAIL', ''),

  /**
   * Phone number for testing, use this for all SMS notifications in the MVP
   */
  testPhone: getEnvVar('TEST_PHONE', '+447700900000'),

  /**
   * Token expiration time in seconds (72 hours)
   */
  tokenExpirationTime: 60 * 60 * 72,

  /**
   * Feature flags to enable/disable certain features
   */
  features: {
    /**
     * If true, all emails will be sent to the testEmail regardless of the actual recipient
     * This should be true for development and testing, false for production
     */
    useTestEmailForAll: getEnvVar('USE_TEST_EMAIL_FOR_ALL', 'false') === 'true',

    /**
     * If true, logs additional debugging information to the console
     */
    enableDebugLogging: !isProd(),

    /**
     * If true, show UI features that are still in development
     * This includes the TradesPurple tab bar with Browse and Message tabs
     * Default: show in development, hide in production unless explicitly enabled
     */
    showBetaFeatures: !isProd() || getEnvVar('SHOW_BETA_FEATURES', 'false') === 'true',

    /**
     * If true, show SmartQuote functionality
     * If false, show a "Coming Soon" message instead
     */
    enableSmartQuote: getEnvVar('ENABLE_SMART_QUOTE', 'true') === 'true',

    /**
     * If true, show the manual TradeRequest functionality
     * If false, show a "Coming Soon" message instead
     */
    enableTradeRequest: getEnvVar('ENABLE_TRADE_REQUEST', 'true') === 'true'
  }
};
